import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingOverlay, useToast } from '../components/ui';
import { OutlineCreationModal } from '../components/OutlineCreationModal';
import {
  getWorkQueue,
  updateOutline,
  createOutline,
  getPillars,
  getPlans,
  getSchedulerTasks,
  generateOutlines,
  getJobStatus,
  getBillingSubscription,
} from '../api';
import type { WorkQueueRow } from '../api';
import type { ContentOutline, ContentPillar, ContentPlan, PageKey, ScheduledTask } from '../types';
import { pollJobStatus } from '../utils/jobPolling';
import './CalendarPage.css';

/* ═══════════════════════════════════════════════════════════════════════════
   Types & helpers
   ═══════════════════════════════════════════════════════════════════════════ */

type ViewMode = 'month' | 'week';

type FilterState = {
  status: string;
  format: string;
};

const STATUS_LABELS: Record<string, string> = {
  needs_approval: 'Awaiting Approval',
  needs_content: 'Draft',
  needs_media: 'Needs Media',
  ready_to_schedule: 'Ready',
  in_progress: 'In Progress',
  scheduled: 'Scheduled',
  published: 'Published',
  failed: 'Failed',
};

const STATUS_DOTS: Record<string, string> = {
  needs_approval: 'var(--color-neutral-400)',
  needs_content: 'var(--color-neutral-400)',
  needs_media: 'var(--color-warning-main)',
  in_progress: 'var(--color-warning-main)',
  ready_to_schedule: 'var(--color-info-main)',
  scheduled: 'var(--color-scheduled-main)',
  published: 'var(--color-success-main)',
  failed: 'var(--color-error-main)',
};

const FORMAT_ICONS: Record<string, string> = {
  photo: '📷',
  video: '🎬',
  carousel: '🎠',
  reel: '🎥',
  story: '📖',
};

function formatIcon(fmt: string): string {
  return FORMAT_ICONS[fmt?.toLowerCase()] ?? '📄';
}

/** Use local-time getters so the calendar cell dates always match the device timezone. */
function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Returns true when the ISO string has a meaningful time (non-midnight). */
function hasSpecificTime(isoStr: string | null): boolean {
  if (!isoStr) return false;
  const d = new Date(isoStr);
  return d.getHours() !== 0 || d.getMinutes() !== 0;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function startOfWeek(d: Date): Date {
  const day = d.getDay(); // 0=Sun
  return addDays(d, -day);
}

/** Returns an array of Date objects for all visible cells in a month calendar
 *  (always starts on Sunday, 6 weeks × 7 days = 42 cells). */
function monthCells(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const startOffset = first.getDay(); // 0=Sun
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    cells.push(addDays(first, i - startOffset));
  }
  return cells;
}

function weekCells(anchor: Date): Date[] {
  const sun = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => addDays(sun, i));
}

function fmtMonthYear(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function fmtWeekRange(days: Date[]): string {
  const first = days[0];
  const last = days[6];
  if (first.getMonth() === last.getMonth()) {
    return `${first.toLocaleDateString('en-US', { month: 'long' })} ${first.getDate()}–${last.getDate()}, ${first.getFullYear()}`;
  }
  return `${first.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${last.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function fmtDateFull(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Pick the best date for a row to place it on the calendar */
function rowDate(row: WorkQueueRow): string | null {
  return row.published_at ?? row.publish_at ?? row.scheduled_for ?? null;
}

function isRowPublished(row: WorkQueueRow): boolean {
  return (
    row.workflow_status === 'published' ||
    row.content_item_status === 'published' ||
    Boolean(row.published_at)
  );
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function hourLabel(h: number): string {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Sub-components
   ═══════════════════════════════════════════════════════════════════════════ */

interface EventDetailProps {
  row: WorkQueueRow;
  onClose: () => void;
  onNavigateContent: (_id: number, _tab: 'preview' | 'edit') => void;
}

function EventDetail({ row, onClose, onNavigateContent }: EventDetailProps) {
  const date = rowDate(row);

  return (
    <div className="cal-popover-backdrop" onClick={onClose}>
      <div className="cal-popover" onClick={(e) => e.stopPropagation()}>
        <div className="cal-popover__header">
          <div>
            <div className="cal-popover__title">{row.title}</div>
            <div className="cal-popover__meta-row" style={{ marginTop: 6 }}>
              <span className={`cal-status-badge cal-status-badge--${row.workflow_status}`}>
                <span
                  className="cal-legend__dot"
                  style={{
                    background: STATUS_DOTS[row.workflow_status] ?? 'var(--color-neutral-400)',
                  }}
                />
                {STATUS_LABELS[row.workflow_status] ?? row.workflow_status}
              </span>
              <span className="cal-format-badge">
                {formatIcon(row.content_format)} {row.content_format}
              </span>
            </div>
          </div>
          <button className="cal-popover__close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="cal-popover__body">
          {row.summary && <p className="cal-popover__summary">{row.summary}</p>}
          <div className="cal-popover__meta-row">
            {date && (
              <div className="cal-popover__date">
                <span>📅</span>
                <span>{fmtDateFull(date)}</span>
                {fmtTime(date) && <span>at {fmtTime(date)}</span>}
              </div>
            )}
          </div>
        </div>

        <div className="cal-popover__footer">
          <button
            className="cal-popover__action-btn cal-popover__action-btn--primary"
            onClick={() => {
              onClose();
              onNavigateContent(row.id, 'edit');
            }}
          >
            ✏️ Edit
          </button>
          <button
            className="cal-popover__action-btn cal-popover__action-btn--secondary"
            onClick={() => {
              onClose();
              onNavigateContent(row.id, 'preview');
            }}
          >
            👁 Preview
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Month chip ─────────────────────────────────────────────────────────── */
interface ChipProps {
  row: WorkQueueRow;
  onDragStart: (_e: React.DragEvent, _id: number) => void;
  onDragEnd: () => void;
  draggingId: number | null;
  isRescheduling: boolean;
  isPublishLocked: boolean;
  onClick: (_row: WorkQueueRow) => void;
}

function EventChip({
  row,
  onDragStart,
  onDragEnd,
  draggingId,
  isRescheduling,
  isPublishLocked,
  onClick,
}: ChipProps) {
  const date = rowDate(row);
  return (
    <div
      className={`cal-chip cal-chip--${row.workflow_status}${draggingId === row.id ? ' cal-chip--dragging' : ''}${isRescheduling ? ' cal-chip--rescheduling' : ''}`}
      draggable={!isRescheduling && !isPublishLocked}
      onDragStart={(e) => onDragStart(e, row.id)}
      onDragEnd={onDragEnd}
      onClick={() => onClick(row)}
      title={isPublishLocked ? `${row.title} (Published posts can't be rescheduled)` : row.title}
    >
      <span className="cal-chip__format-icon">{formatIcon(row.content_format)}</span>
      <span className="cal-chip__title">{row.title}</span>
      {date && <span className="cal-chip__time">{fmtTime(date)}</span>}
    </div>
  );
}

/* ── Week event block ──────────────────────────────────────────────────── */
interface WeekEventProps {
  row: WorkQueueRow;
  draggingId: number | null;
  isRescheduling: boolean;
  isPublishLocked: boolean;
  onDragStart: (_e: React.DragEvent, _id: number) => void;
  onDragEnd: () => void;
  onClick: (_row: WorkQueueRow) => void;
}

function WeekEvent({
  row,
  draggingId,
  isRescheduling,
  isPublishLocked,
  onDragStart,
  onDragEnd,
  onClick,
}: WeekEventProps) {
  const date = rowDate(row);
  return (
    <div
      className={`cal-week-event cal-week-event--${row.workflow_status}${draggingId === row.id ? ' cal-week-event--dragging' : ''}${isRescheduling ? ' cal-week-event--rescheduling' : ''}`}
      draggable={!isRescheduling && !isPublishLocked}
      onDragStart={(e) => onDragStart(e, row.id)}
      onDragEnd={onDragEnd}
      onClick={() => onClick(row)}
    >
      <div className="cal-week-event__title">
        {formatIcon(row.content_format)} {row.title}
      </div>
      <div className="cal-week-event__meta">
        <span
          className={`cal-status-badge cal-status-badge--${row.workflow_status}`}
          style={{ padding: '1px 5px' }}
        >
          {STATUS_LABELS[row.workflow_status] ?? row.workflow_status}
        </span>
        {date && <span>{fmtTime(date)}</span>}
      </div>
    </div>
  );
}

/* ── Task chip ─────────────────────────────────────────────────────────── */
const TASK_ICONS: Record<string, string> = {
  CREATE_ITEM: '✍️',
  CREATE_MEDIA: '🖼️',
  PUBLISH: '🚀',
};
const TASK_SHORT: Record<string, string> = {
  CREATE_ITEM: 'Write',
  CREATE_MEDIA: 'Media',
  PUBLISH: 'Publish',
};

interface TaskChipProps {
  task: ScheduledTask;
}

function TaskChip({ task }: TaskChipProps) {
  const label = task.outline_title
    ? `${TASK_SHORT[task.task_type] ?? task.task_type}: ${task.outline_title}`
    : (TASK_SHORT[task.task_type] ?? task.task_type);
  return (
    <div
      className={`cal-task-chip cal-task-chip--${task.status}`}
      title={`${task.task_type_display} · ${task.status_display}${task.outline_title ? ` · ${task.outline_title}` : ''}`}
    >
      <span className="cal-task-chip__icon">{TASK_ICONS[task.task_type] ?? '⚙️'}</span>
      <span className="cal-task-chip__label">{label}</span>
    </div>
  );
}

/* ── Day-click outline creation modal (replaced by OutlineCreationModal) ── */

function calcDropTime(e: React.DragEvent): { hours: number; minutes: number } {
  const rect = e.currentTarget.getBoundingClientRect();
  const relY = Math.max(0, e.clientY - rect.top);
  // 1 hour = WEEK_HOUR_HEIGHT_PX (60px), snap to 15-minute slots
  const rawMinutes = Math.round((relY / WEEK_HOUR_HEIGHT_PX) * 60 / 15) * 15;
  const totalMinutes = Math.max(0, Math.min(23 * 60 + 45, rawMinutes));
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}

function fmtDropTime(hours: number, minutes: number): string {
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h = hours % 12 === 0 ? 12 : hours % 12;
  return `${h}:${String(minutes).padStart(2, '0')} ${ampm}`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Main CalendarPage
   ═══════════════════════════════════════════════════════════════════════════ */

type CalendarPageProps = {
  workspaceId: number;
  onNavigate?: (_page: PageKey) => void;
  autoApprove?: boolean;
};

const MAX_CHIPS_PER_DAY = 3;
const MAX_TASK_CHIPS_MONTH = 3;
const MAX_TASK_CHIPS_WEEK = 2;
const DEFAULT_PUBLISH_HOUR = '09:00:00';
/** Must match `.cal-week__hour-line { height: 60px }` in CalendarPage.css */
const WEEK_HOUR_HEIGHT_PX = 60;
const TIMED_EVENT_HEIGHT_PX = WEEK_HOUR_HEIGHT_PX - 2;
/** Fetch a generous batch; the API has no date-range filter so we load all at once.
 *  Pagination can be added when needed. */
const CALENDAR_PAGE_SIZE = 500;

/** Formats a Date as "Sunday, April 5, 2026." for the AI notes pre-fill. */
function friendlyDateLabel(d: Date): string {
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function CalendarPage({ workspaceId, onNavigate, autoApprove }: CalendarPageProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [view, setView] = useState<ViewMode>('month');
  const [anchor, setAnchor] = useState<Date>(new Date());
  const [rows, setRows] = useState<WorkQueueRow[]>([]);
  const [tasks, setTasks] = useState<ScheduledTask[]>([]);
  const [pillars, setPillars] = useState<ContentPillar[]>([]);
  const [plans, setPlans] = useState<ContentPlan[]>([]);
  const [allowedFormats, setAllowedFormats] = useState<string[]>(['image', 'carousel', 'video']);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filters, setFilters] = useState<FilterState>({ status: '', format: '' });
  const [selected, setSelected] = useState<WorkQueueRow | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dragOverDate, setDragOverDate] = useState<string | null>(null);
  const [dragOverTime, setDragOverTime] = useState<{ dateStr: string; hours: number; minutes: number } | null>(null);
  const [reschedulingIds, setReschedulingIds] = useState<Set<number>>(new Set());
  const [showTasks, setShowTasks] = useState(false);
  /** Date the user clicked on to create a new outline */
  const [creationDate, setCreationDate] = useState<Date | null>(null);
  const [showCreationModal, setShowCreationModal] = useState(false);
  /** Date keys that should show all overflow events */
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const cleanupRef = useRef<(() => void) | null>(null);

  /* ── Data loading ────────────────────────────────────────────────────── */
  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      getWorkQueue({ workspace_id: workspaceId, page_size: CALENDAR_PAGE_SIZE }),
      getSchedulerTasks(workspaceId),
      getPillars(),
      getPlans(),
      getBillingSubscription(workspaceId).catch(() => null),
    ])
      .then(([queueRes, tasksRes, pillarsRes, plansRes, subscription]) => {
        setRows(queueRes.results ?? []);
        setTasks(tasksRes.results ?? []);
        setPillars(pillarsRes.results ?? []);
        setPlans(plansRes.results ?? []);
        const entitled = (
          (subscription?.entitlements?.formats as string[] | undefined) ?? []
        ).filter((f) => ['image', 'carousel', 'video'].includes(f));
        setAllowedFormats(entitled.length > 0 ? entitled : ['image', 'carousel', 'video']);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => {
      if (cleanupRef.current) cleanupRef.current();
    };
  }, []);

  /* ── Filtered rows (only those that have a date) ──────────────────── */
  const filteredRows = rows.filter((r) => {
    const d = rowDate(r);
    if (!d) return false;
    if (filters.status && r.workflow_status !== filters.status) return false;
    if (filters.format && r.content_format?.toLowerCase() !== filters.format) return false;
    return true;
  });

  /* Build a map: isoDate (local) → rows
   * Use new Date() + local isoDate() to avoid UTC timezone off-by-one bugs. */
  const byDate = filteredRows.reduce<Record<string, WorkQueueRow[]>>((acc, r) => {
    const d = rowDate(r)!;
    const key = isoDate(new Date(d));
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  /* Build a map: isoDate (local) → tasks */
  const tasksByDate = tasks
    .filter((t) => t.status !== 'canceled')
    .reduce<Record<string, ScheduledTask[]>>((acc, t) => {
      if (!t.scheduled_for) return acc;
      const key = isoDate(new Date(t.scheduled_for));
      if (!acc[key]) acc[key] = [];
      acc[key].push(t);
      return acc;
    }, {});

  /* ── Available filter options ────────────────────────────────────────── */
  const allStatuses = Array.from(new Set(rows.map((r) => r.workflow_status)));
  const allFormats = Array.from(
    new Set(rows.map((r) => r.content_format?.toLowerCase()).filter(Boolean))
  );

  /* ── Navigation ──────────────────────────────────────────────────────── */
  const prev = () => {
    if (view === 'month') {
      setAnchor((a) => new Date(a.getFullYear(), a.getMonth() - 1, 1));
    } else {
      setAnchor((a) => addDays(startOfWeek(a), -7));
    }
  };
  const next = () => {
    if (view === 'month') {
      setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + 1, 1));
    } else {
      setAnchor((a) => addDays(startOfWeek(a), 7));
    }
  };
  const goToday = () => setAnchor(new Date());

  /* ── Period label ──────────────────────────────────────────────────── */
  const periodLabel = view === 'month' ? fmtMonthYear(anchor) : fmtWeekRange(weekCells(anchor));

  /* ── Drag & drop ────────────────────────────────────────────────────── */
  const handleDragStart = useCallback(
    (e: React.DragEvent, id: number) => {
      const row = rows.find((r) => r.id === id);
      if (row && isRowPublished(row)) {
        e.preventDefault();
        toast("Published posts can't be rescheduled in the calendar.", 'error');
        return;
      }
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(id));
      setDraggingId(id);
    },
    [rows, toast]
  );

  const handleDragOver = useCallback((e: React.DragEvent, dateStr: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(dateStr);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverDate(null);
    setDragOverTime(null);
  }, []);

  const handleDrop = useCallback(
    async (
      e: React.DragEvent,
      dateStr: string,
      dropTime?: { hours: number; minutes: number }
    ) => {
      e.preventDefault();
      const id = Number(e.dataTransfer.getData('text/plain'));
      setDraggingId(null);
      setDragOverDate(null);
      setDragOverTime(null);
      if (!id) return;

      const row = rows.find((r) => r.id === id);
      if (!row) return;
      if (isRowPublished(row)) {
        toast("Published posts can't be rescheduled in the calendar.", 'error');
        return;
      }

      // Build the new ISO timestamp
      let newIso: string;
      const existingDate = rowDate(row);

      if (dropTime !== undefined) {
        // Timed drop in week view: use the exact slot the user dropped on
        const target = new Date(dateStr);
        target.setHours(dropTime.hours, dropTime.minutes, 0, 0);
        newIso = target.toISOString();
      } else if (existingDate) {
        // Date-only drop (month or all-day row): preserve existing time, change date
        const existing = new Date(existingDate);
        const [y, m, d] = dateStr.split('-').map(Number);
        existing.setFullYear(y, m - 1, d);
        newIso = existing.toISOString();
      } else {
        newIso = new Date(`${dateStr}T${DEFAULT_PUBLISH_HOUR}`).toISOString();
      }

      // Prevent rescheduling to a past date or time (60s buffer for clock skew)
      if (new Date(newIso).getTime() < Date.now() - 60_000) {
        toast(
          "You can't reschedule a post to a past date or time. Please choose a future slot.",
          'error'
        );
        return;
      }

      // Optimistic update of rows
      setReschedulingIds((prev) => new Set(prev).add(id));
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, publish_at: newIso, scheduled_for: newIso } : r))
      );

      try {
        // Backend PATCH cascades publish_at to pending scheduler tasks (CREATE_ITEM, CREATE_MEDIA, PUBLISH)
        await updateOutline(id, { publish_at: newIso });
        toast(`Rescheduled to ${fmtDateFull(newIso)} at ${fmtTime(newIso)}.`, 'success');
        // Refresh queue + tasks so status and task dates are fully in sync after backend recompute
        Promise.all([
          getWorkQueue({ workspace_id: workspaceId, page_size: CALENDAR_PAGE_SIZE }),
          getSchedulerTasks(workspaceId),
        ])
          .then(([queueRes, tasksRes]) => {
            setRows(queueRes.results ?? []);
            setTasks(tasksRes.results ?? []);
          })
          .catch(console.error);
      } catch (err) {
        console.error('Failed to update outline date:', err);
        // Revert optimistic update
        setRows((prev) =>
          prev.map((r) =>
            r.id === id
              ? { ...r, publish_at: row.publish_at, scheduled_for: row.scheduled_for }
              : r
          )
        );
        const msg = err instanceof Error ? err.message : 'Failed to reschedule post';
        toast(msg, 'error');
      } finally {
        setReschedulingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [rows, workspaceId, toast]
  );

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverDate(null);
    setDragOverTime(null);
  };

  /* ── Navigate to content detail ───────────────────────────────────── */
  const handleNavigateContent = (id: number, tab: 'preview' | 'edit') => {
    navigate(`/content/${id}?tab=${tab}`);
  };

  /* ── Create post for a specific day — opens OutlineCreationModal ──── */
  const handleDayClick = (date: Date) => {
    setCreationDate(date);
    setShowCreationModal(true);
  };

  /* ── Outline creation handlers for OutlineCreationModal ─────────────── */
  const handleCreateOwn = async (data: {
    title: string;
    summary: string;
    content_format: string;
    pillar: number | null;
    plan: number | null;
    publish_at: string | null;
  }) => {
    await createOutline(workspaceId, {
      title: data.title,
      summary: data.summary,
      content_format: data.content_format,
      pillar: data.pillar ?? undefined,
      plan: data.plan ?? undefined,
      publish_at: data.publish_at ?? undefined,
      status: 'draft',
    } as Partial<ContentOutline>);
    setShowCreationModal(false);
    setCreationDate(null);
    loadData();
  };

  const handleCreateWithAI = async (data: {
    plan: number | null;
    notes: string;
    outlines: Array<{
      pillar: number | null;
      content_format: string;
      forced_publish_at: string | null;
      force_date_instruction: string | null;
    }>;
  }) => {
    setGenerating(true);
    try {
      // Quick mode sends no per-post specs — the agent infers count and dates.
      // Specific mode sends one outline_request per card.
      const requestData: Record<string, unknown> = { notes: data.notes };
      if (data.outlines.length > 0) {
        requestData.outline_requests = data.outlines.map((outline) => ({
          pillar_id: outline.pillar,
          content_format: outline.content_format,
          forced_publish_at: outline.forced_publish_at,
          force_date_instruction: outline.force_date_instruction,
        }));
      }
      if (data.plan) requestData.plan_id = data.plan;

      const jobResponse = await generateOutlines(workspaceId, requestData);
      cleanupRef.current = pollJobStatus(
        jobResponse.job_id,
        getJobStatus,
        () => {
          setGenerating(false);
          setShowCreationModal(false);
          setCreationDate(null);
          loadData();
        },
        () => {
          setGenerating(false);
        }
      );
    } catch (err) {
      setGenerating(false);
      throw err;
    }
  };

  /* ═══════════════════════════════════════════════════════════════════════
     Month view
     ═══════════════════════════════════════════════════════════════════════ */
  const renderMonth = () => {
    const cells = monthCells(anchor.getFullYear(), anchor.getMonth());
    const currMonth = anchor.getMonth();

    return (
      <div className="cal-month">
        {/* Day-of-week header */}
        <div className="cal-month__dow-row">
          {DOW.map((d) => (
            <div key={d} className="cal-month__dow">
              {d}
            </div>
          ))}
        </div>

        {/* Grid */}
        <div className="cal-month__grid">
          {cells.map((cell) => {
            const key = isoDate(cell);
            const isToday = isoDate(cell) === isoDate(today);
            const isOutside = cell.getMonth() !== currMonth;
            const isDragOver = dragOverDate === key;
            const cellRows = byDate[key] ?? [];
            const isExpanded = expandedDays.has(key);
            const visible = isExpanded ? cellRows : cellRows.slice(0, MAX_CHIPS_PER_DAY);
            const overflow = cellRows.length - MAX_CHIPS_PER_DAY;
            const cellTasks = showTasks ? (tasksByDate[key] ?? []) : [];

            return (
              <div
                key={key}
                className={[
                  'cal-day',
                  isOutside ? 'cal-day--outside' : '',
                  isToday ? 'cal-day--today' : '',
                  isDragOver ? 'cal-day--drag-over' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onDragOver={(e) => handleDragOver(e, key)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, key)}
                onClick={(e) => {
                  const t = e.target as HTMLElement;
                  if (t.closest('.cal-chip')) return;
                  if (t.closest('.cal-task-chip')) return;
                  if (t.closest('.cal-day__overflow')) return;
                  // Collapse expanded day on background click
                  if (isExpanded) {
                    setExpandedDays((prev) => {
                      const s = new Set(prev);
                      s.delete(key);
                      return s;
                    });
                    return;
                  }
                  handleDayClick(cell);
                }}
                title={`Create post for ${cell.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`}
              >
                <div className="cal-day__num">{cell.getDate()}</div>
                <div className="cal-day__events">
                  {visible.map((r) => (
                    <EventChip
                      key={r.id}
                      row={r}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      draggingId={draggingId}
                      isRescheduling={reschedulingIds.has(r.id)}
                      isPublishLocked={isRowPublished(r)}
                      onClick={setSelected}
                    />
                  ))}
                  {!isExpanded && overflow > 0 && (
                    <div
                      className="cal-day__overflow"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedDays((prev) => new Set(prev).add(key));
                      }}
                    >
                      +{overflow} more
                    </div>
                  )}
                  {isExpanded && (
                    <div
                      className="cal-day__overflow"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedDays((prev) => {
                          const s = new Set(prev);
                          s.delete(key);
                          return s;
                        });
                      }}
                    >
                      show less
                    </div>
                  )}
                </div>
                {cellTasks.length > 0 && (
                  <div className="cal-day__tasks">
                    {cellTasks.slice(0, MAX_TASK_CHIPS_MONTH).map((t) => (
                      <TaskChip key={t.id} task={t} />
                    ))}
                    {cellTasks.length > MAX_TASK_CHIPS_MONTH && (
                      <div className="cal-day__overflow" style={{ pointerEvents: 'none' }}>
                        +{cellTasks.length - MAX_TASK_CHIPS_MONTH} tasks
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════════════════════
     Week view
     ═══════════════════════════════════════════════════════════════════════ */
  const renderWeek = () => {
    const days = weekCells(anchor);

    return (
      <div className="cal-week">
        {/* Header row */}
        <div className="cal-week__header-row">
          <div className="cal-week__gutter" />
          {days.map((d) => {
            const isToday = isoDate(d) === isoDate(today);
            return (
              <div
                key={isoDate(d)}
                className={`cal-week__day-head${isToday ? ' cal-week__day-head--today' : ''}`}
              >
                <div className="cal-week__day-name">{DOW[d.getDay()]}</div>
                <div className="cal-week__day-num">{d.getDate()}</div>
              </div>
            );
          })}
        </div>

        {/* All-day row — shows events without a specific time */}
        <div className="cal-week__allday-row">
          <div className="cal-week__allday-gutter">
            <span className="cal-week__allday-label">all‑day</span>
          </div>
          {days.map((d) => {
            const key = isoDate(d);
            const isDragOver = dragOverDate === key;
            // Only all-day / no-specific-time events go in this row
            const alldayRows = (byDate[key] ?? []).filter((r) => !hasSpecificTime(rowDate(r)));
            const cellTasks = showTasks ? (tasksByDate[key] ?? []) : [];
            return (
              <div
                key={key}
                className={`cal-week__allday-cell${isDragOver ? ' cal-week__allday-cell--drag-over' : ''}`}
                onDragOver={(e) => handleDragOver(e, key)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, key)}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('.cal-week-event')) return;
                  if ((e.target as HTMLElement).closest('.cal-task-chip')) return;
                  handleDayClick(d);
                }}
                title={`Create post for ${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`}
              >
                {alldayRows.map((r) => (
                  <WeekEvent
                    key={r.id}
                    row={r}
                    draggingId={draggingId}
                    isRescheduling={reschedulingIds.has(r.id)}
                    isPublishLocked={isRowPublished(r)}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onClick={setSelected}
                  />
                ))}
                {cellTasks.length > 0 && (
                  <div className="cal-day__tasks" style={{ marginTop: alldayRows.length ? 4 : 0 }}>
                    {cellTasks.slice(0, MAX_TASK_CHIPS_WEEK).map((t) => (
                      <TaskChip key={t.id} task={t} />
                    ))}
                    {cellTasks.length > MAX_TASK_CHIPS_WEEK && (
                      <div className="cal-day__overflow" style={{ pointerEvents: 'none' }}>
                        +{cellTasks.length - MAX_TASK_CHIPS_WEEK} tasks
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Time grid — timed events are positioned absolutely at their hour */}
        <div className="cal-week__body">
          <div className="cal-week__time-col">
            {HOURS.map((h) => (
              <div key={h} className="cal-week__time-slot">
                <span className="cal-week__time-label">{hourLabel(h)}</span>
              </div>
            ))}
          </div>
          {days.map((d) => {
            const key = isoDate(d);
            const isToday = isoDate(d) === isoDate(today);
            const isDragOver = dragOverDate === `${key}-timed`;
            // Only events with a specific time go in the time grid
            const timedRows = (byDate[key] ?? []).filter((r) => hasSpecificTime(rowDate(r)));
            return (
              <div
                key={key}
                className={[
                  'cal-week__day-col',
                  isToday ? 'cal-week__day-col--today' : '',
                  isDragOver ? 'cal-week__day-col--drag-over' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDragOverDate(`${key}-timed`);
                  const t = calcDropTime(e);
                  setDragOverTime({ dateStr: key, ...t });
                }}
                onDragLeave={handleDragLeave}
                onDrop={(e) => {
                  const t = calcDropTime(e);
                  handleDrop(e, key, t);
                }}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('.cal-week-event-timed')) return;
                  handleDayClick(d);
                }}
                title={`Create post for ${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`}
              >
                {HOURS.map((h) => (
                  <div key={h} className="cal-week__hour-line" />
                ))}
                {/* Time drop indicator */}
                {dragOverTime?.dateStr === key && (
                  <div
                    className="cal-week__drop-indicator"
                    style={{
                      top: dragOverTime.hours * WEEK_HOUR_HEIGHT_PX + (dragOverTime.minutes / 60) * WEEK_HOUR_HEIGHT_PX,
                    }}
                  >
                    <span className="cal-week__drop-indicator-label">
                      {fmtDropTime(dragOverTime.hours, dragOverTime.minutes)}
                    </span>
                  </div>
                )}
                {timedRows.map((r) => {
                  const d2 = new Date(rowDate(r)!);
                  const topPx = d2.getHours() * WEEK_HOUR_HEIGHT_PX + d2.getMinutes();
                  const isPublishLocked = isRowPublished(r);
                  return (
                    <div
                      key={r.id}
                      className={`cal-week-event-timed cal-week-event-timed--${r.workflow_status}${draggingId === r.id ? ' cal-week-event-timed--dragging' : ''}${reschedulingIds.has(r.id) ? ' cal-week-event-timed--rescheduling' : ''}`}
                      style={{ top: topPx, height: TIMED_EVENT_HEIGHT_PX }}
                      draggable={!reschedulingIds.has(r.id) && !isPublishLocked}
                      onDragStart={(e) => handleDragStart(e, r.id)}
                      onDragEnd={handleDragEnd}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(r);
                      }}
                      title={
                        isPublishLocked
                          ? `${r.title} (Published posts can't be rescheduled)`
                          : r.title
                      }
                    >
                      <div className="cal-week-event__title">
                        {formatIcon(r.content_format)} {r.title}
                      </div>
                      <div className="cal-week-event__meta">
                        <span>{fmtTime(rowDate(r))}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ─────────────────────────────────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="cal-page">
        <div className="page-header">
          <h1 className="page-header__title">Calendar</h1>
        </div>
        <LoadingOverlay label="Loading calendar…" />
      </div>
    );
  }

  return (
    <div className="cal-page">
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="cal-header">
        <div className="cal-header__left">
          <h1 className="cal-header__title">Content Calendar</h1>
          <p className="cal-header__subtitle">
            Plan, schedule, and track every post across your content pipeline.
          </p>
        </div>
        <div className="cal-header__right">
          <button className="cal-create-btn" onClick={() => onNavigate?.('content')}>
            <span className="cal-create-btn__icon">+</span>
            Create content
          </button>
        </div>
      </div>

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="cal-toolbar">
        <div className="cal-toolbar__nav">
          <button className="cal-nav-btn" onClick={prev} aria-label="Previous">
            ‹
          </button>
          <button className="cal-today-btn" onClick={goToday}>
            Today
          </button>
          <span className="cal-nav-period">{periodLabel}</span>
          <button className="cal-nav-btn" onClick={next} aria-label="Next">
            ›
          </button>
        </div>

        <div className="cal-toolbar__filters">
          <select
            className="cal-filter-select"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">All statuses</option>
            {allStatuses.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s] ?? s}
              </option>
            ))}
          </select>

          <select
            className="cal-filter-select"
            value={filters.format}
            onChange={(e) => setFilters((f) => ({ ...f, format: e.target.value }))}
          >
            <option value="">All formats</option>
            {allFormats.map((f) => (
              <option key={f} value={f}>
                {formatIcon(f)} {f}
              </option>
            ))}
          </select>
        </div>

        <div className="cal-toolbar__view-toggle">
          <button
            className={`cal-view-btn${view === 'month' ? ' cal-view-btn--active' : ''}`}
            onClick={() => setView('month')}
          >
            Month
          </button>
          <button
            className={`cal-view-btn${view === 'week' ? ' cal-view-btn--active' : ''}`}
            onClick={() => setView('week')}
          >
            Week
          </button>
          <button
            className={`cal-tasks-toggle${showTasks ? ' cal-tasks-toggle--active' : ''}`}
            onClick={() => setShowTasks((v) => !v)}
            title={showTasks ? 'Hide scheduled tasks' : 'Show scheduled tasks'}
            style={{ marginLeft: 8 }}
          >
            ⚙️ {showTasks ? 'Tasks on' : 'Tasks off'}
          </button>
        </div>
      </div>

      {/* ── Legend ──────────────────────────────────────────────────────── */}
      <div className="cal-legend">
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <div key={key} className="cal-legend__item">
            <span
              className="cal-legend__dot"
              style={{ background: STATUS_DOTS[key] ?? 'var(--color-neutral-400)' }}
            />
            {label}
          </div>
        ))}
      </div>

      {/* ── Calendar body ───────────────────────────────────────────────── */}
      {filteredRows.length === 0 && !loading ? (
        <div className="cal-empty">
          <div className="cal-empty__icon">📅</div>
          <div className="cal-empty__title">No content scheduled</div>
          <p className="cal-empty__body">
            Set a publish date on your content outlines and they'll appear here. You can also drag
            cards to reschedule them.
          </p>
        </div>
      ) : view === 'month' ? (
        renderMonth()
      ) : (
        renderWeek()
      )}

      {/* ── Detail popover ──────────────────────────────────────────────── */}
      {selected && (
        <EventDetail
          row={selected}
          onClose={() => setSelected(null)}
          onNavigateContent={handleNavigateContent}
        />
      )}

      {/* ── Outline creation modal (shared with ContentPage) ─────────────── */}
      {showCreationModal && (
        <OutlineCreationModal
          pillars={pillars}
          plans={plans}
          allowedFormats={allowedFormats}
          initialMode="specific"
          initialNotes={
            creationDate ? `Create the post for ${friendlyDateLabel(creationDate)}. ` : undefined
          }
          initialForcedDate={creationDate ? isoDate(creationDate) : undefined}
          autoApprove={autoApprove}
          scheduledDate={creationDate}
          onClose={() => {
            if (!generating) {
              setShowCreationModal(false);
              setCreationDate(null);
            }
          }}
          onCreateOwn={handleCreateOwn}
          onCreateWithAI={handleCreateWithAI}
          loading={generating}
        />
      )}
    </div>
  );
}

export default CalendarPage;
